<template>
    <v-card flat>

        <v-row
            class="pa-4"
            justify="space-between"
        >
            <v-col cols="12">
                <v-sheet class="pa-4 primary">
                    <v-text-field
                        v-model="search"
                        label="Search Menu"
                        dark
                        flat
                        solo-inverted
                        hide-details
                        clearable
                        clear-icon="mdi-close-circle-outline"
                    ></v-text-field>
                    <v-checkbox
                        v-show="false"
                        v-model="caseSensitive"
                        dark
                        hide-details
                        label="Case sensitive search"
                    ></v-checkbox>
                </v-sheet>
            </v-col>
            <v-col cols="5">
                <v-treeview
                    :active.sync="active"
                    :items="[menu]"
                    :search="search"
                    :filter="filter"
                    activatable
                    hoverable
                    transition
                    open-all
                    return-object
                    @update:active="editSmartNav"
                >
                    <template v-slot:label="{item,leaf,selected,indeterminate,active,open}">
                        <span class="menu-name">{{ item.name || "** NEW MENU ITEM **" }}</span>
                    </template>
                </v-treeview>
            </v-col>

            <v-divider vertical></v-divider>

            <v-col
                class="d-flex text-center"
            >
                <v-scroll-y-transition mode="out-in">
                    <div
                        v-if="!active.length"
                        class="text-h6 grey--text text--lighten-1 font-weight-light"
                        style="align-self: center;"
                    >
                        Select a Menu Item
                    </div>
                    <v-card
                        v-else
                        :key="active_object.id"
                        class="pt-6 mx-auto"
                        flat
                        max-width="400"
                    >
                        <v-card-text>

                            <h3 class="text-h5 mb-2">
                                {{ active_object.name }}
                                <menu-burger-menu
                                    @NEW="createNewChild"
                                    @DELETE="deleteActiveObject"
                                />
                            </h3>
                            <v-divider></v-divider>
                            <br/>
                            <v-text-field
                                v-model="active_object.name"
                                label="name"
                            ></v-text-field>
                            <v-text-field
                                v-model="active_object.sequence"
                                v-show="!is_top"
                                type="number"
                                :min="1"
                                :max="max_sequence"
                                label="Sequence"
                            ></v-text-field>
                            <v-combobox
                                v-model="active_object.name_route"
                                v-show="!is_top"
                                :items="routes_vl"
                                label="route"
                            ></v-combobox>
                            <v-text-field
                                v-model="active_object.name_l10n_key"
                                v-show="!is_top"
                                label="localization key"
                            ></v-text-field>
                            <v-text-field
                                v-model="active_object.name_icon"
                                :append-icon="getIconName(active_object.name_icon)"
                                v-show="!is_top"
                                label="icon"
                            ></v-text-field>
                            <h3>Permissions</h3>
                            <chip-select
                                value
                                label="Whitelist"
                                :selection.sync="active_whitelist"
                                :valuelist="vl_roles"
                            />
                            <chip-select
                                value
                                label="Blacklist"
                                :selection.sync="active_blacklist"
                                :valuelist="vl_roles"
                            />

                        </v-card-text>
                        <v-divider></v-divider>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="primary" @click="saveSmartNav">Save</v-btn>
                        </v-card-actions>

                    </v-card>
                </v-scroll-y-transition>
            </v-col>
        </v-row>
    </v-card>
</template>

<script>
import MenuBurgerMenu from "@/SmartNav/SmartNavItemBurgerMenu";
import ChipSelect from "@/Generic/ChipSelect";
export default {
    name: "SmartNavMaker",
    components: {ChipSelect, MenuBurgerMenu},
    props: {
        'input': {
            // The menu to be displayed
            type: Object,
            required: false,
            default: function () {
                return undefined
            },
        },
        'name': {
            // The name of the menu to be AJAX-fetched
            type: String,
            required: false,
            default: function () {
                return "MAIN"
            },
        },
        'endpoint': {
            // Specify this url if not passing `input` into this component
            type: String,
            required: false,
            default: function () {
                return undefined
            },
        },
        'endpoint_method': {
            // Specify this METHOD/VERB if not passing `input` into this component
            type: String,
            required: false,
            default: function () {
                return 'post'
            },
        },
        'constants': {
            type: Object,
            required: false,
            default: function () {
                return undefined
            }
        },

    },
    data () {
        return {
            menu: this.input || null,
            active: [],
            open: [],
            active_whitelist: [],
            active_blacklist: [],
            search: null,
            caseSensitive: false,
            searchTree: (fn) => (obj) =>
                Array.isArray(obj)
                    ? obj.length == 0
                        ? null
                        : this.searchTree (fn) (obj [0]) || this.searchTree (fn) (obj .slice (1))
                    : fn (
                        obj,
                        () => this.searchTree (fn) (obj .children || []),
                        () => obj
                    ),
        }
    },

    computed: {
        // The item currently selected in the treeview
        active_object () {
            if (!this.active.length) return undefined
            return this.active[0]
        },
        // Indicates if active object is a top-level item
        is_top () {
            return !this.active_object?.parent_id
        },
        // Turns the named routes into a String[]
        routes_vl () {
            return Object.keys(route().ziggy.namedRoutes)
        },
        // Indicates what the max value for the sequence input should be based on the current active object
        max_sequence() {
            let parent = this.findId(this.active_object.parent_id)
            return parent?.children?.length
        },
        // Turns user roles into a v-select-style data structure
        vl_roles() {
            return Object.values(this.constants.USER_ROLES)?.map(role => ({text:role, value:role}))
        },
        filter () {
            return this.caseSensitive
                ? (item, search, textKey) => item[textKey].indexOf(search) > -1
                : undefined
        },
    },
    mounted() {
        this.fetchMenu()
    },
    methods:{
        // Will AJAX-fetch the entire smartnav system to display in the treeview
        async fetchMenu(force=false) {
            if (this.menu && !force) {
                return true
            }
            let url = route(this.endpoint, this.name).url()
            axios({
                url:url,
                method:'post'
            }).then((response)=>{
                this.$set(this, 'menu', response.data)
            })
        },
        // Refetch and reactivate
        async refreshMenu() {
            let active_object_id = this.active_object?.id
            this.active.length = 0
            await this.fetchMenu(true)
                .then(response => {
                    if (active_object_id) {
                        this.editSmartNav([this.findId(active_object_id)])
                        this.$emit('refresh')
                    }
                })

        },
        // Performs some housekeeping tasks when an item gets selected from the tree
        editSmartNav(item) {
            let selected = item[0]
            this.$set(this, 'active_whitelist', selected?.whitelist)
            this.$set(this, 'active_blacklist', selected?.blacklist)
        },
        // Will send an AJAX request to save the currently active tree item
        saveSmartNav() {
            let payload = structuredClone(this.active_object)
            payload.whitelist = this.active_whitelist
            payload.blacklist = this.active_blacklist

            axios.put(route('smartnav.update', this.active_object).url(), payload)
                .then(response => {
                    this.$root.snackbar.success("Menu item updated successfully")
                    this.$nextTick(() => {
                        this.refreshMenu()
                    })
                }).catch(error => {
                    this.$root.snackbar.error(error)
                })
        },
        // Recursively search the tree for an id
        findId(target) {
            return this.searchTree (
                (node, next, found) => node.id === target ? found () : next(),
            ) (this.menu)
        },
        // Create a new descendant from the currently active item
        createNewChild() {
            // console.log("Add code to create new child record from here")
            let new_smartnav = {
                parent_id: this.active_object?.id
            }
            axios.post(route('smartnav.store', this.active_object).url(), new_smartnav)
                .then(response => {
                    this.$root.snackbar.success("Menu item created successfully")
                    this.$nextTick(() => {
                        this.refreshMenu()
                    })
                }).catch(error => {
                this.$root.snackbar.error(error)
            })
        },
        // Send a DELETE request to the server for the currently active item
        deleteActiveObject() {
            // console.log("Add code to delete this node and its descendants")
            axios.delete(route('smartnav.destroy', this.active_object).url())
                .then(response => {
                    this.$root.snackbar.success("Menu item deleted successfully")
                    this.$nextTick(() => {
                        this.refreshMenu()
                    })
                }).catch(error => {
                    this.$root.snackbar.error(error)
            })
        },
        // Prefixes the icon name when appropriate
        getIconName(partial_name) {
            return partial_name ? `mdi-${partial_name}` : null
        },
    }
}
</script>

<style scoped>
    .flex-start-content {
        justify-content: flex-start;
    }

    .menu-name {
        align-items: center;
        border-radius: 4px;
        display: inline-flex;
        flex: 0 0 auto;
        font-weight: 500;
        letter-spacing: .0892857143em;
        justify-content: center;
        outline: 0;
        position: relative;
        text-decoration: none;
        text-indent: .0892857143em;
        text-transform: uppercase;
    }
</style>
